import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api';
import { Alert, Button } from '../../../components/ui';
import { toast } from 'sonner';

interface GitMember {
  id: number; username: string; name?: string; access_level: number;
  entity_id: string | null; pairing_error?: string;
}
interface Status {
  ok: boolean; error?: string; code?: string; can_configure?: boolean; management_error?: string;
  members?: GitMember[]; members_complete?: boolean; pairing_complete?: boolean;
  /** Lidé spárovaní přes SSO, které GitLab mezi členy repozitáře nemá. */
  paired_candidates?: GitMember[];
  acm_members?: Array<{ entity_id: string; role: string }>;
  contract?: { status: string; result?: { error?: string; code?: string;
    issues?: Array<{ entity_id: string; code: string }> } } | null;
}
export function useMemberSyncStatus(slug: string) {
  return useQuery({ queryKey: ['project-member-sync', slug], staleTime: 10_000,
    queryFn: async () => {
      const result = await api.mcp<Status>('codable.gitlab_members_configure', { slug }, { silent: true });
      if (!result.ok) throw new Error(result.error ?? result.code ?? 'GitLab members unavailable');
      return result;
    },
  });
}
function role(level: number) {
  return level >= 50 ? 'Owner' : level >= 40 ? 'Maintainer' : level >= 30 ? 'Developer'
    : level >= 20 ? 'Reporter' : 'Guest';
}
const levels: Record<string, number> = { read: 20, write: 30, project_admin: 40 };

/**
 * Co o mně říká GITLAB — vedle práv v ACM.
 *
 * Filip 2026-09-10: „Nemůžu zapisovat do main, a přitom mám práva write v ACM
 * jako vector. Tak to je hodně matoucí, kdyby vedle toho byly vidět práva
 * z gitu." Karta zmizela při přepisu 11. 9., přestože tool zůstal registrovaný
 * a klíče překladů v locale — Filip 2026-09-13 ji vrací.
 *
 * Jsou to dvě různé věci. ACM `write` je oprávnění na platformě; členství
 * v GitLabu z něj samo nevzniká, dokud se nezapne synchronizace práv. A i se
 * členstvím rozhoduje o zápisu ještě pravidlo chráněné větve.
 */
interface GitlabAccess {
  ok?: boolean;
  error?: string;
  code?: string;
  branch?: string;
  gitlab_user?: string | null;
  member?: { level: number | null; role: string | null; source: string | null };
  branch_protected?: boolean | null;
  can_push?: boolean | null;
  explain?: string | null;
}

function GitlabRights({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['project-gitlab-access', slug],
    queryFn: () => api.mcp<GitlabAccess>('gitlab.project_access', { slug }, { silent: true }),
    staleTime: 60_000,
  });
  const d = q.data;
  if (q.isLoading || !d) return null;
  if (d.ok === false) {
    // Bez vazby na GitLab není co ukazovat — a není to chyba karty.
    if (d.code === 'no_gitlab_link' || d.code === 'not_connected') return null;
    return <p className='text-xs text-gray-500'>{d.error}</p>;
  }
  return (
    <div className='rounded-lg border border-white/10 bg-black/20 p-2 space-y-1 min-w-0'>
      <div className='text-xs text-gray-300 break-words'>
        {t('codableMembers.glRights', 'V GitLabu')}
        {d.gitlab_user ? <span className='font-mono text-gray-400'>{` (${d.gitlab_user})`}</span> : null}
        {': '}
        <span className='font-mono'>
          {d.member?.role ?? t('codableMembers.glNoMember', 'není členem')}
        </span>
        {d.member?.source === 'inherited'
          ? <span className='text-gray-500'>{t('codableMembers.glInherited', ' (ze skupiny)')}</span>
          : null}
      </div>
      <div className='text-xs text-gray-400 break-words'>
        {t('codableMembers.glBranch', 'Větev')} <span className='font-mono'>{d.branch}</span>
        {d.branch_protected
          ? t('codableMembers.glProtected', ' je chráněná')
          : t('codableMembers.glUnprotected', ' není chráněná')}
        {'; '}
        {d.can_push === true
          ? t('codableMembers.glCanPush', 'zapisovat můžeš')
          : d.can_push === false
          ? t('codableMembers.glCannotPush', 'zapisovat nemůžeš')
          : t('codableMembers.glPushUnknown', 'právo zápisu se nepodařilo zjistit')}
      </div>
      {d.explain ? <p className='text-xs text-amber-200/90 whitespace-pre-wrap'>{d.explain}</p> : null}
    </div>
  );
}

export function ProjectMemberSync({ slug }: { slug: string }) {
  const { t } = useTranslation(); const qc = useQueryClient();
  const query = useMemberSyncStatus(slug);
  const [selected, setSelected] = useState<string[]>([]);
  const data = query.data;
  const paired = data?.members?.filter((m) => m.entity_id) ?? [];
  const unpaired = data?.members?.filter((m) => !m.entity_id) ?? [];
  // Spárovaní lidé z ACM, které GitLab mezi členy repozitáře nemá.
  const missingInGitlab = (data?.paired_candidates ?? []).filter((m) => m.entity_id);
  // Explicitní generika: bez nich react-query odvodí TError jako {} a
  // `action.error?.message` pak není typově dostupné — přitom právě ta
  // hláška je smyslem téhle změny (důvod, ne „bez uvedení důvodu").
  const action = useMutation<void, Error, 'sync' | 'import'>({
    mutationFn: async (kind: 'sync' | 'import') => {
      const result = await api.mcp<{
        ok?: boolean;
        error?: string;
        code?: string;
        issues?: Array<{ code: string; entity_id?: string }>;
      }>(
        kind === 'import' ? 'codable.gitlab_members_configure' : 'codable.gitlab_members_reconcile',
        kind === 'import' ? { slug, confirm: true, import_entities: selected } : { slug });
      if (!result.ok) {
        // Engine vrací důvody jako kódy (NO_PERSONAL_MEMBERS…), ne větu —
        // „tool vrátil ok:false bez uvedení důvodu" nikomu nic neřekne.
        const reasons = [...new Set((result.issues ?? []).map((i) => i.code))]
          .map((code) => t(`codableMembers.syncIssue.${code}`, code));
        throw new Error(
          result.error ?? (reasons.length ? reasons.join('; ') : null) ?? result.code ??
            t('codableMembers.pushFailed', 'Propsání práv selhalo'),
        );
      }
    },
    // Filip 2026-09-13: klik „nic neudělal a okno se nerefreshlo" — chyba se
    // vyhodila, ale nikdo ji nechytil (žádný onError), takže bez toastu i bez
    // obnovy. Výsledek musí být vidět oběma směry.
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
    onSuccess: async (_r, kind) => {
      toast.success(
        kind === 'import'
          ? t('codableMembers.importDone', 'Vybraní členové zapsáni do ACM')
          : t('codableMembers.pushDone', 'Práva propsána do GitLabu'),
      );
      setSelected([]);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['project-member-sync', slug] }),
        qc.invalidateQueries({ queryKey: ['project-members', slug] }),
      ]);
    },
  });
  // Chyba mutace může být cokoli (RQ ji netypuje) — do JSX jde vždy jako text.
  const actionError = action.error
    ? String((action.error as { message?: unknown }).message ?? action.error)
    : null;
  const problems = data?.contract?.result;
  const contractStatus = data?.contract?.status;
  // Plánovač bez osobní identity kontrakt jen odloží; dokončí ho správce tady.
  const awaitingUser = contractStatus === 'awaiting_user';
  const syncNow = (
    <Button variant='secondary' size='sm' disabled={action.isPending || query.isFetching}
      onClick={() => action.mutate('sync')}>
      {t('codableMembers.pushGitlab', 'Propsat práva do GitLabu')}
    </Button>
  );
  return <div className='mt-2 space-y-2 text-xs min-w-0'>
    <h4 className='font-semibold text-gray-200'>GitLab</h4>
    {/* Práva v gitu hned u práv v ACM — jinak „write" v ACM vypadá jako
        příslib zápisu do repozitáře, kterým není. */}
    <GitlabRights slug={slug} />
    {query.isFetching && <p role='status'>{t('common.loading', 'Načítám…')}</p>}
    {contractStatus && !awaitingUser && <p role='status' className='text-gray-400 break-words'>
      {t('codableMembers.syncStateLabel', 'Stav synchronizace')}
      {': '}
      {t(`codableMembers.syncState.${contractStatus}`, contractStatus)}
    </p>}
    {awaitingUser && <Alert variant='warning' className='p-3'>
      <p className='text-xs'>
        {t(
          'codableMembers.syncAwaitingUser',
          'Změna členů čeká na správce — na pozadí se do GitLabu nic nezapisuje. Dokončí se tady, tlačítkem Synchronizovat.',
        )}
      </p>
      {data?.can_configure && <div className='mt-2'>{syncNow}</div>}
    </Alert>}
    {paired.map((member) => {
      const acm = data?.acm_members?.find((m) => m.entity_id === member.entity_id);
      const wanted = acm ? levels[acm.role] : undefined;
      // Nesoulad je jen chybějící právo. Vyšší úroveň v GitLabu je záměr —
      // engine existující přístup nikdy nesnižuje (Filip 2026-09-13).
      const missing = wanted !== undefined && member.access_level < wanted;
      const higher = wanted !== undefined && member.access_level > wanted;
      return <div key={member.id} className='rounded border border-gray-800 p-2 space-y-1'>
        <label className='flex items-center gap-2'>
          {!acm && data?.can_configure && <input type='checkbox'
            checked={selected.includes(member.entity_id!)} disabled={action.isPending}
            onChange={(e) => setSelected((ids) => e.target.checked
              ? [...ids, member.entity_id!] : ids.filter((id) => id !== member.entity_id))} />}
          <span className='truncate'>{member.name ?? member.username}</span>
          <span className='ml-auto text-gray-400'>{role(member.access_level)}</span>
        </label>
        {missing && <p className='text-amber-300' role='alert'>
          {t('codableMembers.roleMismatch', 'Rozdílná práva: ACM {{acm}}, GitLab {{gitlab}}.',
            { acm: acm!.role, gitlab: role(member.access_level) })}
        </p>}
        {higher && <p className='text-gray-400' role='status'>
          {t(
            'codableMembers.roleHigherInGitlab',
            'V GitLabu má vyšší práva ({{gitlab}}) než v ACM ({{acm}}) — synchronizace je nesnižuje.',
            { acm: acm!.role, gitlab: role(member.access_level) },
          )}
        </p>}
      </div>;
    })}
    {missingInGitlab.length > 0 && <div className='rounded border border-gray-800 p-2 space-y-1'>
      <p className='text-gray-300 break-words'>
        {t('codableMembers.pairedMissingInGitlab',
          'Spárovaní členové ACM, kteří v GitLabu chybí ({{count}})',
          { count: missingInGitlab.length })}
      </p>
      {missingInGitlab.map((m) => <p className='truncate text-gray-400' key={m.id}>
        {m.name ?? m.username}
      </p>)}
      {data?.can_configure && syncNow}
    </div>}
    {unpaired.length > 0 && <details>
      <summary className='cursor-pointer text-gray-400'>
        {t('codableMembers.unpairedUsers', 'Nespárovaní uživatelé ({{count}})', { count: unpaired.length })}
      </summary>
      {unpaired.map((m) => <p className='py-1' key={m.id}>{m.name ?? m.username} · {role(m.access_level)}</p>)}
    </details>}
    {data && (data.members_complete === false || data.pairing_complete === false) && <p role='alert' className='text-amber-300'>
      {t('codableMembers.incompleteRoster', 'Seznam nebo párování se nepodařilo ověřit úplně.')}
    </p>}
    {/* Nemožnost zapisovat je problém, ne šedá poznámka pod čarou. */}
    {data && data.ok === false && (data.error || data.code) && <Alert variant='warning' className='p-3'>
      <p className='text-xs'>{String(data.error ?? data.code)}</p>
    </Alert>}
    {actionError && <Alert variant='warning' className='p-3'>
      <p className='text-xs'>{actionError}</p>
    </Alert>}
    {data?.management_error && <Alert variant='warning' className='p-3'>
      <p className='text-xs'>{data.management_error}</p>
    </Alert>}
    <div className='flex flex-wrap gap-2'>
      <Button variant='secondary' size='sm' disabled={query.isFetching || action.isPending}
        onClick={() => void query.refetch()}>{t('codableMembers.syncRefresh', 'Načíst stav')}</Button>
      {data?.can_configure && <>
        {syncNow}
        <Button variant='secondary' size='sm' disabled={!selected.length || action.isPending ||
          !data.members_complete || !data.pairing_complete}
          onClick={() => action.mutate('import')}>{t('codableMembers.importSelected', 'Přidat vybrané do ACM')}</Button>
      </>}
    </div>
    {(query.error || problems?.error || problems?.code) && <p role='alert' className='text-amber-300'>
      {String(query.error?.message ?? problems?.error ?? problems?.code)}
    </p>}
    {problems?.issues?.map((p) => <p role='alert' className='text-amber-300' key={`${p.entity_id}:${p.code}`}>
      {p.entity_id}: {t(`codableMembers.syncIssue.${p.code}`, p.code)}
    </p>)}
  </div>;
}
